import React, { useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './BorderGlow.css';

export interface BorderGlowProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  edgeSensitivity?: number;
  glowColor?: string;
  backgroundColor?: string;
  backgroundColorDark?: string;
  borderRadius?: number;
  glowRadius?: number;
  glowIntensity?: number;
  coneSpread?: number;
  animated?: boolean;
  colors?: string[];
  fillOpacity?: number;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
}

// Colores institucionales primarios de Co Marca:
// Base: Morado Principal (#61257B), Medio: Morado Fucsia (#C1348B), Hotspot en cursor: Oro Acento (#FFD97C)
export const DEFAULT_COMARCA_COLORS = ['#61257B', '#C1348B', '#FFD97C'];

const BorderGlow: React.FC<BorderGlowProps> = ({
  children,
  className = '',
  style = {},
  edgeSensitivity = 65,
  backgroundColor = '#ffffff',
  backgroundColorDark = '#150d24',
  borderRadius = 24,
  glowIntensity = 1,
  coneSpread = 180,
  animated = false,
  colors = DEFAULT_COMARCA_COLORS,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { isDark } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);

  // Estados de animación inercial para movimiento elegante y fluido
  const currentAngleRef = useRef<number>(45);
  const targetAngleRef = useRef<number>(45);
  const currentOpacityRef = useRef<number>(0);
  const targetOpacityRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  const getCenterOfElement = useCallback((el: HTMLElement): [number, number] => {
    const { width, height } = el.getBoundingClientRect();
    return [width / 2, height / 2];
  }, []);

  const getCursorAngle = useCallback((el: HTMLElement, x: number, y: number): number => {
    const [cx, cy] = getCenterOfElement(el);
    const dx = x - cx;
    const dy = y - cy;
    if (dx === 0 && dy === 0) return 0;
    const radians = Math.atan2(dy, dx);
    let degrees = radians * (180 / Math.PI) + 90;
    if (degrees < 0) degrees += 360;
    return degrees;
  }, [getCenterOfElement]);

  const isVisibleRef = useRef<boolean>(true);

  const updateLoop = useCallback(() => {
    const card = cardRef.current;
    if (!card || !isVisibleRef.current) {
      rafRef.current = null;
      return;
    }

    // Calcular la diferencia angular mínima (-180° a +180°) para giros suaves
    let deltaAngle = (targetAngleRef.current - currentAngleRef.current) % 360;
    if (deltaAngle > 180) deltaAngle -= 360;
    if (deltaAngle < -180) deltaAngle += 360;

    // Suavizado lerp inercial
    currentAngleRef.current += deltaAngle * 0.18;
    currentOpacityRef.current += (targetOpacityRef.current - currentOpacityRef.current) * 0.18;

    // Si el objetivo es 0 y ya bajó al mínimo perceptible, detener el ciclo (0% CPU)
    if (targetOpacityRef.current === 0 && currentOpacityRef.current < 0.008) {
      currentOpacityRef.current = 0;
      card.style.setProperty('--glow-opacity', '0');
      rafRef.current = null;
      return;
    }

    // Calcular el inicio del arco para que el hotspot quede exactamente en el ángulo del cursor
    // Con coneSpread = 180°, abarca exactamente la mitad de la tarjeta
    const halfCone = coneSpread / 2;
    const startAngle = currentAngleRef.current - halfCone;

    card.style.setProperty('--cursor-angle', `${currentAngleRef.current.toFixed(1)}deg`);
    card.style.setProperty('--glow-start-angle', `${startAngle.toFixed(1)}deg`);
    card.style.setProperty('--glow-opacity', `${currentOpacityRef.current.toFixed(3)}`);

    rafRef.current = requestAnimationFrame(updateLoop);
  }, [coneSpread]);

  const startLoop = useCallback(() => {
    if (!rafRef.current && isVisibleRef.current) {
      rafRef.current = requestAnimationFrame(updateLoop);
    }
  }, [updateLoop]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calcular distancia al borde más cercano
    const distLeft = x;
    const distRight = rect.width - x;
    const distTop = y;
    const distBottom = rect.height - y;
    const distToEdge = Math.min(distLeft, distRight, distTop, distBottom);

    // Limitar sensibilidad máxima al 45% del ancho/alto para tarjetas pequeñas
    const maxThreshold = Math.min(rect.width, rect.height) * 0.45;
    const threshold = Math.min(edgeSensitivity, maxThreshold);

    if (distToEdge <= threshold && distToEdge >= 0) {
      // El cursor está cerca del borde: activar glow proporcional a la cercanía
      const proximity = 1 - (distToEdge / threshold);
      const intensity = Math.min(1, Math.max(0, proximity * 1.5)) * glowIntensity;
      targetAngleRef.current = getCursorAngle(card, x, y);
      targetOpacityRef.current = intensity;
    } else {
      // El cursor está en el centro/interior de la tarjeta: apagar glow
      targetOpacityRef.current = 0;
    }

    startLoop();
  }, [edgeSensitivity, getCursorAngle, glowIntensity, startLoop]);

  const handlePointerLeave = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    targetOpacityRef.current = 0;
    startLoop();
    if (onMouseLeave) onMouseLeave(e);
  }, [onMouseLeave, startLoop]);

  const handlePointerEnter = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (card) {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const angle = getCursorAngle(card, x, y);
      targetAngleRef.current = angle;
      currentAngleRef.current = angle;

      const distLeft = x;
      const distRight = rect.width - x;
      const distTop = y;
      const distBottom = rect.height - y;
      const distToEdge = Math.min(distLeft, distRight, distTop, distBottom);
      const maxThreshold = Math.min(rect.width, rect.height) * 0.45;
      const threshold = Math.min(edgeSensitivity, maxThreshold);

      if (distToEdge <= threshold && distToEdge >= 0) {
        const proximity = 1 - (distToEdge / threshold);
        targetOpacityRef.current = Math.min(1, Math.max(0, proximity * 1.5)) * glowIntensity;
      } else {
        targetOpacityRef.current = 0;
      }
      startLoop();
    }
    if (onMouseEnter) onMouseEnter(e);
  }, [edgeSensitivity, getCursorAngle, glowIntensity, onMouseEnter, startLoop]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    // Usar IntersectionObserver para optimizar rendimiento (solo animar si está en pantalla)
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting && targetOpacityRef.current > 0) {
          startLoop();
        } else if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      },
      { threshold: 0.05 }
    );

    observer.observe(card);

    return () => {
      observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [startLoop]);

  useEffect(() => {
    if (!animated || !cardRef.current) return;
    const card = cardRef.current;
    card.classList.add('sweep-active');

    let currentAngle = 0;
    const sweepInterval = setInterval(() => {
      currentAngle = (currentAngle + 2.5) % 360;
      card.style.setProperty('--cursor-angle', `${currentAngle}deg`);
      card.style.setProperty('--glow-start-angle', `${(currentAngle - 90)}deg`);
    }, 16);

    const timeout = setTimeout(() => {
      clearInterval(sweepInterval);
      card.classList.remove('sweep-active');
    }, 3200);

    return () => {
      clearInterval(sweepInterval);
      clearTimeout(timeout);
    };
  }, [animated]);

  // Colores primarios institucionales estándar:
  // hotspot = Oro Acento (#FFD97C)
  // c1 = Morado Fucsia (#C1348B)
  // c2 = Morado Corporativo (#61257B)
  const hotspot = colors.length >= 3 ? colors[2] : (colors[0] || '#FFD97C');
  const c1 = colors.length >= 2 ? colors[1] : '#C1348B';
  const c2 = colors.length >= 1 ? colors[0] : '#61257B';

  // Resolver fondo: si backgroundColor es el default '#ffffff' y estamos en modo oscuro, usar backgroundColorDark
  const resolvedBg = backgroundColor === '#ffffff' && isDark ? backgroundColorDark : backgroundColor;

  return (
    <div
      ref={cardRef}
      onPointerMove={handlePointerMove}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onClick={onClick}
      className={`border-glow-card ${isDark ? 'border-glow-card--dark' : ''} ${className}`}
      style={{
        '--border-radius': `${borderRadius}px`,
        '--card-bg': resolvedBg,
        '--card-bg-dark': backgroundColorDark,
        '--glow-hotspot': hotspot,
        '--glow-c1': c1,
        '--glow-c2': c2,
        ...style,
      } as React.CSSProperties}
    >
      {/* 1. Aro de luz perimetral sobre el borde (100% hueco al centro, abarca la mitad hacia el cursor) */}
      <div className="border-glow-ring" aria-hidden="true" />

      {/* 2. Halo difuminado exterior (proyecta aura hacia afuera de la tarjeta) */}
      <div className="border-glow-aura" aria-hidden="true" />

      {/* 3. Interior de la tarjeta (100% limpio y nítido, sin lavado de color) */}
      <div className="border-glow-inner">
        {children}
      </div>
    </div>
  );
};

export default BorderGlow;
